import React, { useEffect, useRef } from "react";
import { FaCheck, FaSave, FaTimes, FaUndo } from "react-icons/fa";

import {
  RESULT_RANK_OPTIONS,
  RESULT_TOKENS,
  formatResultDate,
} from "../utils/resultatsUtils";

function EditResultModal({
  isOpen,
  event,
  form,
  saving = false,
  onClose,
  onChange,
  onSubmit,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const addResult = (value) => {
    onChange("resultats", `${form.resultats || ""}${value}`);
  };

  const removeLastResult = () => {
    onChange("resultats", String(form.resultats || "").slice(0, -1));
  };

  const clearResults = () => {
    onChange("resultats", "");
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-amber-500 to-orange-600 p-5 text-white dark:border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/70">
                Modifier un résultat
              </p>

              <h2 className="mt-2 font-titre text-2xl font-bold">
                {event?.titre || "Compétition"}
              </h2>

              <p className="mt-2 text-sm font-medium text-white/75">
                {formatResultDate(event?.start)}
              </p>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-white/30"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
            <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <label
                    className="text-sm font-bold text-slate-950 dark:text-white"
                    htmlFor="resultats"
                  >
                    Résultats des combats
                  </label>

                  <p className="mt-1 text-xs font-medium leading-5 text-slate-500 dark:text-white/50">
                    ✅ gagné, ❌ perdu, N nul, F forfait, D disqualifié.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={clearResults}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                >
                  <FaUndo />
                  Vider
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {RESULT_TOKENS.map((token) => (
                  <button
                    key={token.value}
                    type="button"
                    onClick={() => addResult(token.value)}
                    className={`inline-flex min-h-11 items-center justify-center rounded-2xl px-4 py-2 text-sm font-bold transition hover:-translate-y-0.5 ${token.className}`}
                    title={token.label}
                  >
                    {token.value}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={removeLastResult}
                  className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-red-600 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5"
                >
                  Supprimer
                </button>
              </div>

              <input
                id="resultats"
                name="resultats"
                type="text"
                value={form.resultats}
                readOnly
                className="mt-4 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-lg font-bold tracking-[0.35em] text-slate-800 outline-none dark:border-white/10 dark:bg-slate-950 dark:text-white"
              />
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Classement
                </span>

                <select
                  name="classement"
                  value={form.classement}
                  onChange={onChange}
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
                >
                  <option value="">Sélectionnez un classement</option>
                  {RESULT_RANK_OPTIONS.map((rank) => (
                    <option key={rank} value={rank}>
                      {rank}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Lien du podium
                </span>

                <input
                  type="text"
                  name="podium_url"
                  value={form.podium_url}
                  onChange={onChange}
                  placeholder="https://..."
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
                />
              </label>
            </section>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Lien du tableau
              </span>

              <input
                type="text"
                name="tableau_url"
                value={form.tableau_url}
                onChange={onChange}
                placeholder="https://..."
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Observations
              </span>

              <textarea
                name="observations"
                value={form.observations}
                onChange={onChange}
                rows={5}
                placeholder="Note personnelle, déroulé de la compétition, points à travailler..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-7 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
              />
            </label>
          </div>

          <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              {saving ? <FaSave className="animate-pulse" /> : <FaCheck />}
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditResultModal;